import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { BookingProvider, useBooking } from './hooks/useBooking';
import { extractSubdomain } from './utils/firebase';

import { HomePage } from './pages/HomePage';
import { SiteColorVariables } from './hooks/useSiteColors';
import { StickyBookButton } from './pages/sections/StickyBookButton';
import { ServiceSelectionPage } from './pages/ServiceSelectionPage';
import { DateTimeSelectionPage } from './pages/DateTimeSelectionPage';
import { CustomerDetailsPage } from './pages/CustomerDetailsPage';
import { BookingConfirmationPage } from './pages/BookingConfirmationPage';
import { SuccessPage } from './pages/SuccessPage';
import { PaymentPage } from './pages/PaymentPage';
import { ReturnPage } from './pages/ReturnPage';

import { LandingPage } from './pages/landing/LandingPage';
import { ThanksPage } from './pages/landing/ThanksPage';
import { PrivacyPage } from './pages/landing/PrivacyPage';
import { SchoolLandingPage } from './pages/school/SchoolLandingPage';
import { FormPage } from './pages/FormPage';
import { BookingFormsPage } from './pages/BookingFormsPage';
import { useEffect, useMemo, useState } from 'react';

/**
 * Thin gate that reads loading/error state from context.
 * Data fetching happens once in BookingProvider — this just shows
 * a loading screen or error while the provider is working.
 */
function DataGate({ children }: { children: React.ReactNode }) {
  const { dataLoading, dataError } = useBooking();

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Getting help from Sky</p>
        </div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Unable to Load
          </h1>
          <p className="text-slate-600 mb-6">{dataError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-slate-800 text-white font-semibold px-6 py-3 rounded-xl hover:bg-slate-900 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AppHostStripeBridgePage({ mode }: { mode: 'complete' | 'refresh' | 'fallback' }) {
  const [showFallbackCta, setShowFallbackCta] = useState(false);
  const schemeUrl = useMemo(() => {
    if (mode === 'complete') return 'sky://stripe/onboarding-complete';
    if (mode === 'refresh') return 'sky://stripe/onboarding-refresh';
    return 'sky://stripe';
  }, [mode]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Help From Sky';
    let rafId = 0;
    let isPageHidden = false;
    const startedAt = performance.now();

    const handleVisibilityChange = () => {
      isPageHidden = document.visibilityState !== 'visible';
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.location.href = schemeUrl;

    const checkFallback = () => {
      if (!isPageHidden && performance.now() - startedAt >= 1000) {
        setShowFallbackCta(true);
        return;
      }
      if (!isPageHidden) {
        rafId = window.requestAnimationFrame(checkFallback);
      }
    };
    rafId = window.requestAnimationFrame(checkFallback);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.title = previousTitle;
    };
  }, [mode, schemeUrl]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-start justify-start p-6 pt-16">
      <video
        src="/swipe-up-arrows.webm"
        autoPlay
        loop
        muted
        playsInline
        className="w-24 h-auto mb-4"
      />
      <div className="max-w-sm">
        <h1 className="text-2xl font-bold text-slate-900 mb-3">Almost there!</h1>
        {showFallbackCta ? (
          <p className="text-slate-600 text-lg">
            Tap the <strong>✓</strong> button in the top left corner to return to the app.
          </p>
        ) : (
          <p className="text-slate-600">Trying to open the app now...</p>
        )}
      </div>
    </div>
  );
}

function App() {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  // Read at render time: the router's catch-all <Navigate> rewrites the URL
  // (dropping the query string) before the redirect effect below runs
  const searchAndHash = window.location.search + window.location.hash;
  const isAppHost = hostname === 'app.helpfromsky.com';
  const extractedSubdomain = extractSubdomain(hostname);
  const isSubdomain = !!extractedSubdomain;

  // Apex domain (helpfromsky.com) — load the static landing page
  if (!isSubdomain) {
    // For local dev and as a fallback, handle root redirection
    useEffect(() => {
      if (pathname === '/' || pathname === '/index.html') {
        // Keep the query string so UTM tags from QR codes reach analytics
        window.location.href = '/landingpage.html' + searchAndHash;
      }
    }, [pathname, searchAndHash]);

    return (
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* We keep these as React routes for now, or until static equivalents exist */}
          <Route path="/thanks" element={<ThanksPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/school/:slug" element={<SchoolLandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (isAppHost) {
    return (
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/stripe/onboarding-complete" element={<AppHostStripeBridgePage mode="complete" />} />
          <Route path="/stripe/onboarding-refresh" element={<AppHostStripeBridgePage mode="refresh" />} />
          <Route path="/stripe/*" element={<AppHostStripeBridgePage mode="fallback" />} />
          <Route path="*" element={<AppHostStripeBridgePage mode="fallback" />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Subdomain (business-name.helpfromsky.com) — render booking app
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Standalone form page — fetches its own data, no BookingProvider needed */}
        <Route path="/forms/:formCode" element={<FormPage />} />

        {/* All booking routes — wrapped in BookingProvider + DataGate */}
        <Route path="*" element={
          <BookingProvider>
            <DataGate>
              <>
              <SiteColorVariables />
              <StickyBookButton />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/book" element={<ServiceSelectionPage />} />
                <Route path="/book/date-time" element={<DateTimeSelectionPage />} />
                <Route path="/book/details" element={<CustomerDetailsPage />} />
                <Route path="/book/forms" element={<BookingFormsPage />} />
                <Route path="/book/confirm" element={<BookingConfirmationPage />} />
                <Route path="/book/success" element={<SuccessPage />} />
                <Route path="/book/payment" element={<PaymentPage />} />
                <Route path="/book/return" element={<ReturnPage />} />

                {/* Fallback */}
                <Route path="*" element={
                  <div className="min-h-screen bg-site-bg flex items-center justify-center p-4">
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-site-text mb-2">Page Not Found</h1>
                      <p className="text-site-muted mb-4">The page you're looking for doesn't exist.</p>
                      <a href="/" className="text-site-link font-medium underline">
                        Return to Home
                      </a>
                    </div>
                  </div>
                } />
              </Routes>
              </>
            </DataGate>
          </BookingProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
